import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@classes/extends';
import { RFSurface, Surface, Surfaces, SurfacesMillesime } from '@estel-numerique/galaclick-api';
import { ValidatorDateService, ValidatorPatternService } from '@estel-numerique/galaclick-core';
import { Constantes } from '@services/util/constantes';
import { lastValueFrom } from 'rxjs';
import { FicheClientCampagnesComponent, FicheClientCampagnesComponentData } from './ficheclient-campagnes/ficheclient-campagnes.component';
import { AddButton } from '../../../composants/composants';


@Component({
    selector: 'client-surfaces',
    templateUrl: 'ficheclient-surfaces.component.html',
    imports: [AddButton]
})
export class FicheClientSurfacesComponent extends BaseComponent implements OnInit {
    public validDate = inject(ValidatorDateService);
    public validPattern = inject(ValidatorPatternService);
    private formBuilder = inject(UntypedFormBuilder);
    private dialog = inject(MatDialog);

    public codeModule: string = Constantes.CodeOption.CFICHECLIENT;

    public titre: string;
    public surface: Surfaces;
    public allRfSurfaces: RFSurface[];
    public idClient: number;
    public surfaceSelectionnee: string;
    public formAjout: UntypedFormGroup;

    protected lgTrad: Record<string, string>;

    constructor() {
        super();
        this.formAjout = this.formBuilder.group({
            TYPESERV: [null, [Validators.required]]
        });
        this.lgTrad = {
            GRPTECHNIQUES: this.translate.instant("APPFICHECLIENT.COLTAB.GRPTECHNIQUES"),
            ENGAGEMENTS: this.translate.instant("APPFICHECLIENT.COLTAB.ENGAGEMENTS"),
            ATELIERLAIT: this.translate.instant('APPFICHECLIENT.COLTAB.ATELIERLAIT'),
            SURFACES: this.translate.instant('APPFICHECLIENT.SURFACES'),
            TYPE: this.translate.instant('TYPE'),
            TOTAL: this.translate.instant('TOTAL'),
            NOM: this.translate.instant("NOM"),
            RET: this.translate.instant("RET"),
        };
    }


    // =========================================================================
    // CHARGEMENT CONTEXTE
    // =========================================================================

    ngOnInit(): void {
        super.ngOnInit();
    }

    async initialiseSurfaces(surface: Surfaces, idClient: number) {
        this.surface = surface;
        this.idClient = idClient;
        await this.initListe();
    }

    async initListe() {
        try {
            this.allRfSurfaces = await lastValueFrom(this.wsCacheLow.getRefSurface());
        } catch (e) {
            this.util.fErreur(e);
        }
    }

    public getData(data: number) {
        return ((data > 0 && data != null) ? data : '--');
    }

    public openSurface(surfaceSelectionnee: string) {
        const oData: FicheClientCampagnesComponentData = {
            idClient: this.idClient,
            surfaces: null,
            rfSurfacesListe: this.surface.RFSURFACES,
            allRfSurfaces: this.allRfSurfaces
        };
        this.surfaceSelectionnee = surfaceSelectionnee;
        switch (surfaceSelectionnee) {
            case 'A':
                oData.surfaces = this.surface.SURFACEA;
                break;
            case 'B':
                oData.surfaces = this.surface.SURFACEB;
                break;
            case 'C':
                oData.surfaces = this.surface.SURFACEC;
                break;
        }
        if (oData) {
            const dialogCampagne = this.dialog.open(FicheClientCampagnesComponent, { data: oData });
            dialogCampagne.componentInstance.saved.subscribe((value) => {
                this.savedSurfaces(value);
            });
        }
    }

    public savedSurfaces(surfaces: SurfacesMillesime) {
        switch (this.surfaceSelectionnee) {
            case 'A':
                this.surface.SURFACEA = surfaces;
                break;
            case 'B':
                this.surface.SURFACEB = surfaces;
                break;
            case 'C':
                this.surface.SURFACEC = surfaces;
                break;
        }

        this.surface.RFSURFACES = [];
        this.getListeRfSurfacesClient(this.surface.RFSURFACES, this.surface.SURFACEA.SURFACES);
        this.getListeRfSurfacesClient(this.surface.RFSURFACES, this.surface.SURFACEB.SURFACES);
        this.getListeRfSurfacesClient(this.surface.RFSURFACES, this.surface.SURFACEC.SURFACES);

        this.surface.SURFACEA.SURFACES = this.getListeSurfacesComplete(this.surface.RFSURFACES, this.surface.SURFACEA.SURFACES);
        this.surface.SURFACEB.SURFACES = this.getListeSurfacesComplete(this.surface.RFSURFACES, this.surface.SURFACEB.SURFACES);
        this.surface.SURFACEC.SURFACES = this.getListeSurfacesComplete(this.surface.RFSURFACES, this.surface.SURFACEC.SURFACES);
    }

    private getListeRfSurfacesClient(rfSurfaces: RFSurface[], listeTmp: Surface[]) {
        listeTmp.forEach((surface: Surface) => {
            if (surface.RFSURFACE) {
                const rfSurface = rfSurfaces.find((rf: RFSurface) => {
                    return rf.IDRFSURFACE == surface.RFSURFACE.IDRFSURFACE;
                });
                if (!rfSurface) {
                    rfSurfaces.push(this.allRfSurfaces.find((rf: RFSurface) => { return surface.RFSURFACE.IDRFSURFACE == rf.IDRFSURFACE; }));
                }
            }
        });
        if (rfSurfaces.length > 0) {
            rfSurfaces.sort((r1, r2) => { return r1.ORDREGLOBAL - r2.ORDREGLOBAL; });
        }
    }

    private getListeSurfacesComplete(rfSurfaces: RFSurface[], listeTmp: Surface[]) {
        const surfaces: Surface[] = [];
        rfSurfaces.forEach((rf: RFSurface) => {
            const surface: Surface = listeTmp.find((surf: Surface) => {
                return surf.RFSURFACE?.IDRFSURFACE == rf.IDRFSURFACE;
            });
            // Si l'élément surf de la rfSurfaces est bien présent dans la listeTmp on l'ajoute
            if (surface) {
                surfaces.push(surface);
            } else {
                surfaces.push({
                    IDSURFACECLIENT: null,
                    IDCLIENT: this.idClient,
                    RFSURFACE: null,
                    RFMILLESIME: null,
                    SURFTOTAL: null,
                    RDTTOTAL: null,
                    SURFLAIT: null,
                    RDTLAIT: null,
                });
            }
        });
        return surfaces;
    }

    public checkRfSurfaceRendement(index: number) {
        let indexRf = 0;
        let hasRendement = true;
        this.surface.RFSURFACES.forEach((rf: RFSurface) => {
            if (index == indexRf && rf.RENDEMENT == 0) {
                hasRendement = false;
            }
            indexRf++;
        });
        return hasRendement;
    }

    public checkTabulation(rfSurface: RFSurface) {
        if (rfSurface.IDPARENT == 0) {
            return 0;
        }
        const firstRf: RFSurface = this.surface.RFSURFACES.find((rf: RFSurface) => {
            return rf.IDPARENT == 0;
        });
        if (firstRf) {
            if (rfSurface.IDPARENT == firstRf.IDRFSURFACE) {
                return 1;
            } else {
                let indentation = 3;
                this.surface.RFSURFACES.forEach((rf: RFSurface) => {
                    if (rf.IDPARENT == firstRf.IDRFSURFACE) {
                        this.surface.RFSURFACES.forEach((r: RFSurface) => {
                            if (r.IDPARENT == rf.IDRFSURFACE && r.IDRFSURFACE == rfSurface.IDRFSURFACE) {
                                indentation = 2;
                            }
                        });
                    }
                });
                return indentation;
            }
        }
        return 0;
    }
}
